import styled, {css, type DefaultTheme} from "styled-components";
import type {CSSProperties} from "react";
import {fontFamilies, radii} from "theme/tokens";
import {CAMPAIGN_SIDE_BY_SIDE_MIN_WIDTH_PX} from "home/landing/campaignSpace";
import {SLIDE_OUT_CHIP_PX, SLIDE_OUT_GAP_PX} from "./slideOutCards";

/**
 * The send screen's slide-out card family (kitecorp/transferify-ui#2468 and #2278), built to the
 * owner-approved prototypes (`.claude/prototypes/2468-limit-card-slide-out.html` and
 * `2278-upsell-card-slide-out.html`).
 *
 * ONE shell, two accents. A flat card slides out from behind the send panel to the LEFT, its top edge
 * level with the file row. Tucked, the whole card collapses to its own glyph chip peeking past the
 * panel's edge (owner 2026-09-16 — it used to be a full-height 30px strip, which read as a clipped
 * panel rather than as a tab, and two of them read worse still). Hover nudges the chip 6px further
 * out; click opens; the × tucks it back. No tilt, no swing: the panel lifts 4px (`PeelTag.styles`'
 * Frame rule, asked for with `data-peel-card-lift`) and the card translates.
 *
 * <b>Below the campaign's side-by-side width it hangs under the panel's bottom edge instead.</b> The
 * left slide only fits while the campaign column stands beside the card: in the stacked layout the
 * card is centred and a 720px viewport leaves 159px to its left, less than the 220px the card needs,
 * so the prototype's own 900px threshold — which is that same layout boundary — is the one used here,
 * not the peel family's 720px drop breakpoint (that one has no horizontal constraint to respect).
 */

export const CARD_WIDTH_PX = 208;
export const GAP_PX = SLIDE_OUT_GAP_PX;
/** How much of the tucked card shows past the panel's edge: its glyph chip, exactly. */
export const PEEK_PX = SLIDE_OUT_CHIP_PX;
export const HOVER_NUDGE_PX = 6;
/** The slide, and how long the panel stays lifted (the slide plus a beat to settle). */
export const SLIDE_MS = 620;
export const LIFT_MS = 450;
/** The panel starts rising this long before the card starts sliding. */
export const LIFT_LEAD_MS = 140;
/** On first eligibility the card peeks for this long, then opens once by itself. */
export const AUTO_OPEN_DELAY_MS = 700;

const CLOSED_OFFSET_PX = CARD_WIDTH_PX + GAP_PX - PEEK_PX;

/**
 * The TUCKED chip's corners (owner 2026-09-16): 8px on the side that stands clear of the panel and 4px
 * on the side tucked UNDER it, so the chip reads as something slid behind the panel rather than as a
 * free-standing button. `radii` carries neither figure (9px chip, 12px control), so both are named here.
 */
const TUCKED_RADIUS_OUT = "8px";
const TUCKED_RADIUS_UNDER = "4px";
const slideEase = "cubic-bezier(.2, 1.25, .3, 1)";

/** Which family a card belongs to: the download-limit warning, or the upsell ask. */
export type SlideOutAccent = "warning" | "upsell";

export interface SlideOutPalette {
	surface: string;
	ink: string;
	edge: string;
	action: string;
	actionInk: string;
	track: string;
}

/**
 * The accent's six colours, both modes, from the theme — never a literal at a use site.
 *
 * The upsell family has no action pair of its own: its button and glyph are the app's PRIMARY blue, so
 * a re-pick of the brand moves the card with everything else.
 */
export function slideOutPalette(theme: DefaultTheme, accent: SlideOutAccent): SlideOutPalette {
	const {tokens} = theme;
	return accent === "warning"
		? {
			surface: tokens.amberSurface, ink: tokens.amberInk, edge: tokens.amberEdge,
			action: tokens.amberAction, actionInk: tokens.amberActionInk, track: tokens.amberTrack,
		}
		: {
			surface: tokens.blueWash, ink: tokens.blueWashInk, edge: tokens.blueWashEdge,
			action: tokens.blue, actionInk: tokens.blueInk, track: tokens.blueWashTrack,
		};
}

/** The muted line under the title: the card's own ink, stepped back so the title still leads. */
export function slideOutMutedInk(theme: DefaultTheme, accent: SlideOutAccent): string {
	return `color-mix(in srgb, ${slideOutPalette(theme, accent).ink} 80%, transparent)`;
}

interface AccentProps {
	$accent: SlideOutAccent;
}

interface RootPlacement {
	$top: number;
	$stackedTop: number;
	$stackedLeft: number | null;
	$height: number;
}

export const Root = styled.aside.attrs<RootPlacement>(({$top, $stackedTop, $stackedLeft, $height}) => ({
	style: {
		"--slide-out-card-top": `${$top}px`,
		"--slide-out-stacked-top": `${$stackedTop}px`,
		// Only a tucked chip has a point on the row; the open card spans the panel and reads no offset.
		...($stackedLeft === null ? {} : {"--slide-out-stacked-left": `${$stackedLeft}px`}),
		// The open card's exact height, measured from its content. Explicit on BOTH states on purpose:
		// `height: auto` cannot transition to the chip's 44px, so the card would snap to chip height and
		// then slide, which reads as two separate things happening.
		"--slide-out-card-height": `${$height}px`,
	} as CSSProperties,
}))<AccentProps & RootPlacement & {$open: boolean}>`
	position: absolute;
	z-index: 1;
	top: var(--slide-out-card-top);
	left: ${-(CARD_WIDTH_PX + GAP_PX)}px;
	width: ${CARD_WIDTH_PX}px;
	height: var(--slide-out-card-height);
	box-sizing: border-box;
	padding: 0;
	overflow: hidden;
	border-radius: 14px;
	background: ${({theme, $accent}) => slideOutPalette(theme, $accent).surface};
	color: ${({theme, $accent}) => slideOutPalette(theme, $accent).ink};
	border: 1px solid ${({theme, $accent}) => slideOutPalette(theme, $accent).edge};
	box-shadow: ${({theme}) => theme.tokens.shadowCard};
	font-family: ${fontFamilies.sans};
	text-align: left;
	transform-origin: 100% 50%;
	transform: translateX(${({$open}) => $open ? 0 : CLOSED_OFFSET_PX}px);
	/* The card's own top moves when the card beside it opens or tucks (the stack re-spaces), and a jump
	   there would read as a second, unrelated change — so the top offset travels with the slide. */
	transition: transform ${SLIDE_MS}ms ${slideEase}, top ${SLIDE_MS}ms cubic-bezier(.2, .9, .3, 1),
		left ${SLIDE_MS}ms cubic-bezier(.2, .9, .3, 1), width ${SLIDE_MS}ms cubic-bezier(.2, .9, .3, 1),
		height ${SLIDE_MS}ms cubic-bezier(.2, .9, .3, 1), border-radius ${SLIDE_MS}ms;
	will-change: transform;
	${({$open}) => !$open && css`
		/* Tucked, the card IS its glyph chip: a rounded square, not a clipped panel (owner 2026-09-16).
		   Softer corners on the side standing clear of the panel, tighter ones on the side under it. */
		width: ${PEEK_PX}px;
		height: ${PEEK_PX}px;
		border-radius: ${TUCKED_RADIUS_OUT} ${TUCKED_RADIUS_UNDER} ${TUCKED_RADIUS_UNDER} ${TUCKED_RADIUS_OUT};
		cursor: pointer;
		&:hover { transform: translateX(${CLOSED_OFFSET_PX - HOVER_NUDGE_PX}px); }
	`}

	/* Phones hang the family under the panel's bottom edge instead, placed by slideOutStackedSpots
	   (ui#3227): the open card one gap under the panel at the stacked width, the tucked chips in one row
	   under it. The frame is the containing block and ends where the panel does, so 100% is the panel's
	   bottom edge. Nothing translates here: opening grows the chip into the card where it hangs. */
	@media (max-width: ${CAMPAIGN_SIDE_BY_SIDE_MIN_WIDTH_PX - 1}px) {
		top: calc(100% + var(--slide-out-stacked-top));
		left: 8%;
		width: 84%;
		transform: none;
		${({$open}) => !$open && css`
			/* The chip's place on the row, from the panel's centre line. The corner rule turns with the
			   layout: here the chip's TOP faces the panel, so that is the tight pair. */
			left: calc(50% + var(--slide-out-stacked-left));
			width: ${PEEK_PX}px;
			border-radius: ${TUCKED_RADIUS_UNDER} ${TUCKED_RADIUS_UNDER} ${TUCKED_RADIUS_OUT} ${TUCKED_RADIUS_OUT};
			&:hover { transform: translateY(${HOVER_NUDGE_PX}px); }
		`}
	}

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`;

/**
 * The tucked card's click target: the chip, and only the chip.
 *
 * It covers the whole tucked card because the tucked card is the chip — 44px square, which is also the
 * minimum comfortable touch target. While the card was a full-height strip this had to be clipped to
 * the sliver that showed, because a hit area reaching under the panel has its centre beneath it: a
 * pointer aimed at that centre landed on the panel, the card lost its hover nudge and slid back, and an
 * automated click never found a stable target (the fourth local-stack run of #2468 looped on that).
 */
export const Hit = styled.button<AccentProps>`
	position: absolute;
	inset: 0;
	z-index: 3;
	border: 0;
	border-radius: ${radii.panel};
	background: transparent;
	cursor: pointer;
	&:focus-visible { outline: 2px solid ${({theme, $accent}) => slideOutPalette(theme, $accent).action}; outline-offset: 2px; }
`;

/**
 * The chip inside the OPEN card, bottom-left of its action row. 28px at 10px corners on both cards
 * (owner 2026-09-16). Neither figure is in `radii` — the nearest tokens are 9px and 12px — so they are
 * named constants here rather than a token that would be silently wrong by a pixel.
 */
const INNER_CHIP_PX = 28;
const INNER_CHIP_RADIUS = "10px";
/**
 * The card's glyph chip: bottom-left of the open card, and the whole of the tucked one.
 *
 * ONE glyph in both places (owner 2026-09-16), which is what makes the tuck read as the card shrinking
 * into its own chip rather than as a different control appearing.
 */
export const Chip = styled.span<AccentProps & {$tucked?: boolean}>`
	display: grid;
	place-items: center;
	flex: none;
	width: ${INNER_CHIP_PX}px;
	height: ${INNER_CHIP_PX}px;
	border-radius: ${INNER_CHIP_RADIUS};
	background: ${({theme, $accent}) => slideOutPalette(theme, $accent).track};
	color: ${({theme, $accent}) => slideOutPalette(theme, $accent).action};
	font: 800 16px/1 ${fontFamilies.sans};
	${({$tucked}) => $tucked && css`
		position: absolute;
		inset: 0;
		width: auto;
		height: auto;
		border-radius: inherit;
		background: transparent;
		pointer-events: none;
		z-index: 2;
	`}
`;

/**
 * Everything but the chip: gone while tucked, back once the card has slid clear of the panel.
 *
 * It carries the card's padding and keeps the card's full width while the root narrows to the chip, so
 * the copy never reflows mid-tuck — and its measured height is what the root is given, so the height
 * the card animates between is its own content's rather than a guess.
 */
export const Content = styled.div<{$open: boolean}>`
	/* Never narrower than the card itself, so the copy does not reflow into the 44px chip while the root
	   shrinks around it — a reflowing body would re-measure taller and re-space the whole stack mid-tuck. */
	width: 100%;
	min-width: ${CARD_WIDTH_PX}px;
	box-sizing: border-box;
	padding: 10px 12px;
	opacity: ${({$open}) => $open ? 1 : 0};
	transition: opacity ${({$open}) => $open ? ".25s .25s" : ".15s"};
	${({$open}) => !$open && css`pointer-events: none;`}
	@media (prefers-reduced-motion: reduce) { transition: none; }
`;

export const Close = styled.button<AccentProps>`
	position: absolute;
	top: 5px;
	right: 6px;
	z-index: 1;
	width: 22px;
	height: 22px;
	/* The UA gives every button 1px/6px of padding, and with the app's border-box sizing that left this
	   22px button a CONTENT box of only 10px across. A 14px glyph does not fit a 10px grid area, so it
	   was start-aligned rather than centred — measured 2px right of the button's centre — which is what
	   read as an off-centre ×. Zeroing the padding is the fix; a nudge would only move the symptom.
	   (No backticks in this comment: it sits inside a template literal, and one would end the style.) */
	padding: 0;
	display: grid;
	place-items: center;
	/* Explicit, though a grid item is already blockified: an inline svg sits on the text baseline, so
	   this is what keeps the glyph a centred box rather than a character with a descender under it if
	   the layout above ever stops being grid. */
	& > svg { display: block; }
	border: 0;
	border-radius: 50%;
	background: ${({theme, $accent}) => slideOutPalette(theme, $accent).track};
	color: ${({theme, $accent}) => slideOutPalette(theme, $accent).ink};
	cursor: pointer;
	/* A 44px hit area around a 22px glyph, the same trick the peel tag's action pill uses. */
	&::before { content: ""; position: absolute; inset: -11px; }
	&:hover { background: ${({theme, $accent}) => slideOutPalette(theme, $accent).edge}; }
	&:focus-visible { outline: 2px solid ${({theme, $accent}) => slideOutPalette(theme, $accent).action}; outline-offset: 2px; }
`;

export const Title = styled.h3`
	margin: 0 24px 2px 0;
	font-weight: 700;
	font-size: 13px;
	line-height: 1.2;
	color: inherit;
`;

/** The card's ONE supporting line: what the tier gives, and what it costs. Never a second one. */
export const Line = styled.p<AccentProps>`
	margin: 0 0 8px;
	font-size: 11.5px;
	line-height: 1.3;
	color: ${({theme, $accent}) => slideOutMutedInk(theme, $accent)};
`;

export const Row = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
`;

/**
 * The action row of a card that also offers "Not now" (the upgrade moments, kitecorp/transferify-ui#2909):
 * the two buttons side by side, the same size, wrapping under each other when a long plan name needs the
 * width. No pressure: the way out is as big as the way in.
 */
export const Actions = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px;
`;

export const Action = styled.button<AccentProps>`
	position: relative;
	height: 28px;
	padding: 0 12px;
	border: 0;
	border-radius: ${radii.pill};
	background: ${({theme, $accent}) => slideOutPalette(theme, $accent).action};
	color: ${({theme, $accent}) => slideOutPalette(theme, $accent).actionInk};
	font: 700 12px ${fontFamilies.sans};
	white-space: nowrap;
	cursor: pointer;
	/* Preserve a taller hit area without making the pill visually taller. */
	&::after { content: ""; position: absolute; inset: -8px 0; }
	&:focus-visible { outline: 2px solid ${({theme, $accent}) => slideOutPalette(theme, $accent).action}; outline-offset: 2px; }
`;

/** "Not now": the action's size and shape on the card's quiet track, so declining is as easy as accepting. */
export const Secondary = styled(Action)`
	background: ${({theme, $accent}) => slideOutPalette(theme, $accent).track};
	color: ${({theme, $accent}) => slideOutPalette(theme, $accent).ink};
	&:hover { background: ${({theme, $accent}) => slideOutPalette(theme, $accent).edge}; }
`;
